import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { toast } from 'react-toastify';

import {
  useLogCareCoordinationMutation,
  useOwnProfileQuery,
  usePatientDocumentsQuery,
  usePatientLockMutation,
  usePatientProfileQuery,
  usePatientQuery,
  usePatientTasksListQuery,
  useReplyUnlockPatientRequestMutation,
} from '@common/cache';
import useUserStore from 'store/user.store.ts';
import { IDocumentsTableFilters } from '@views/pages/Patient/components/Documents/hooks';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { usePatientEventListener } from '@views/pages/Patient/hooks/usePatientEventListener.tsx';
import { t } from 'i18next';
import { PatientContext } from '@views/pages/Patient/context';
import {
  PATIENT_TASK_LOCK_PERIOD,
  PATIENT_TASK_LOCK_PERIOD_VARIATION,
} from '@common/types/constants.ts';
import { useDrawerStore } from '@store';

dayjs.extend(utc);
dayjs.extend(timezone);
const minimumHeightLimit = 1024;

const initialFilter: IDocumentsTableFilters = {
  sort_order: 'asc',
  sort_by: 'file_name',
  page: 1,
  limit: 100,
  search: '',
  category: '',
};

const summaryTabIndex = 0;
const taskTabIndex = 5;

export const usePatient = () => {
  const navigate = useNavigate();
  const params = useParams();
  const client = useQueryClient();
  const patient_id = params.patient_id || '';
  const { tabIndex, handleTriggerTabChange } = useContext(PatientContext);
  const { setDrawerOpen, taskId, setTaskId } = useDrawerStore(state => state);

  const { user } = useUserStore();
  const profile = useOwnProfileQuery();

  const {
    data: patient,
    refetch,
    isPending,
    error: patientError,
  } = usePatientQuery({ patient_id });
  const patientLockMutation = usePatientLockMutation(patient_id);
  const replyUnlockPatientRequestMutation =
    useReplyUnlockPatientRequestMutation(patient_id);

  const {
    mutateAsync: logCareCoordinationAsync,
    isPending: isLogCareCoordinationPending,
  } = useLogCareCoordinationMutation(patient_id);

  const { event, handleUnlockLockedPatient, handleClearEvent } =
    usePatientEventListener(patient_id);

  const [isUnlockPatientModalOpen, setIsUnlockPatientModalOpen] =
    useState(false);
  const [requesterName, setRequesterName] = useState('');
  const [isUnlockInProgress, setIsUnlockInProgress] = useState(false);
  const [isCMActivitiesModalOpen, setIsCMActivitiesModalOpen] = useState(false);

  const [currentTime, setCurrentTime] = useState(dayjs());

  const { data: patientProfile } = usePatientProfileQuery({
    patient_id: patient?.id ?? '',
  });

  useEffect(() => {
    if (!axios.isAxiosError(patientError)) return;

    const status = patientError.response?.status;

    if (status === 400) {
      navigate('/something-went-wrong');
    } else if (status === 500) {
      navigate('/server-error');
    }
  }, [patientError]);

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/patient/list');
    }
  };

  const handleCloseCMActivitiesModal = () => {
    setIsCMActivitiesModalOpen(false);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setTaskId('');
  };

  const handleCloseUnlockModal = async () => {
    try {
      await replyUnlockPatientRequestMutation.mutate({ approve: false });

      handleClearEvent();
      setIsUnlockPatientModalOpen(false);
      setRequesterName('');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleLogCareCoordination = async () => {
    try {
      const response = await logCareCoordinationAsync();

      if (response.data.id) {
        setIsCMActivitiesModalOpen(false);
        setTaskId(response.data.id);
        setDrawerOpen(true);
      }
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleApprovePatientUnlock = async () => {
    try {
      await replyUnlockPatientRequestMutation.mutate({ approve: true });

      handleClearEvent();
      setIsUnlockPatientModalOpen(false);
      setRequesterName('');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleUnlockPatientAction = () => {
    handleUnlockLockedPatient();
    setIsUnlockInProgress(true);
  };

  useEffect(() => {
    if (event && event.type === 'patient.unlock_requested') {
      setIsUnlockPatientModalOpen(true);
      setRequesterName(event?.payload?.requester?.full_name);
    }

    if (event && event.type === 'patient.locked') {
      setIsUnlockInProgress(false);
      setIsUnlockPatientModalOpen(false);
      refetch();
    }

    if (event && event.type === 'patient.unlock_reply') {
      refetch();
      if (event.payload.approved) {
        toast.success(t('general.yourUnlockRequestWasApproved'));
      } else {
        toast.error(t('general.yourUnlockRequestWasDenied'));
      }
      setIsUnlockInProgress(false);
    }
  }, [event]);

  useEffect(() => {
    if (patient) {
      document.title = `${patient.name} - Bridge`;
    }

    return () => {
      document.title = 'Bridge';
    };
  }, [patient]);

  const {
    data: documentsData = {
      count: 0,
    },
  } = usePatientDocumentsQuery(patient_id, initialFilter);

  const {
    data: tasksData = {
      count: 0,
    },
  } = usePatientTasksListQuery({ patient_id });

  const isReadOnly = !patient?.has_write_permission;

  const isPatientLocked = patient?.locked_by
    ? patient?.locked_by?.id !== user?.id
    : false;

  const isEditDisabled = isReadOnly || isPatientLocked;

  const smallScreen = window.innerHeight < minimumHeightLimit;
  const tabPanelInstrumentHeight =
    (isEditDisabled ? 50 : 0) +
    (patient?.do_not_call || patient?.outside_business_hours ? 50 : 0) +
    (smallScreen ? 0 : 40) +
    310;

  const handleOpenCMActivitiesModal = () => {
    if (isEditDisabled) return;
    setIsCMActivitiesModalOpen(true);
  };

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    handleTriggerTabChange(newValue);
  };

  const handleLockPatient = async () => {
    await patientLockMutation.mutateAsync();
  };

  useEffect(() => {
    if (
      patient &&
      !isEditDisabled &&
      profile.data?.activity_status === 'active'
    ) {
      handleLockPatient();
    }

    const interval = setInterval(
      () => {
        if (
          patient &&
          !isEditDisabled &&
          profile.data?.activity_status === 'active'
        ) {
          handleLockPatient();
        } else {
          client.invalidateQueries({ queryKey: ['patient'] });
        }
      },
      Math.round(
        PATIENT_TASK_LOCK_PERIOD +
          Math.random() * PATIENT_TASK_LOCK_PERIOD_VARIATION,
      ),
    );

    return () => {
      clearInterval(interval);
    };
  }, [patient, profile.data?.activity_status]);

  useEffect(() => {
    const interval = setInterval(() => {
      const newTime = dayjs();
      if (!newTime.isSame(currentTime, 'minute')) {
        setCurrentTime(newTime);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [currentTime]);

  return {
    patient,
    isPatientDetailsLoading: isPending,
    tabIndex,
    handleChangeTab,
    smallScreen,
    tabPanelInstrumentHeight,
    isPatientLocked,
    isReadOnly,
    isEditDisabled,
    documentsCount: documentsData.count,
    tasksCount: tasksData.count,
    lockedBy: {
      name: patient?.locked_by
        ? `${patient?.locked_by?.first_name || ''} ${patient?.locked_by?.last_name || ''}`
        : '',
      role: patient?.locked_by?.role || '',
    },
    handleBack,
    handleUnlockPatientAction,
    isUnlockPatientModalOpen,
    handleCloseUnlockModal,
    handleApprovePatientUnlock,
    disableUnlockPatient: !patient?.has_write_permission,
    requesterName,
    isUnlockInProgress,
    patientMemoryConnectAppDeviceBattery:
      patient?.memory_connect.tablet_authenticated &&
      patient.memory_connect.battery_percentage &&
      patient.memory_connect.battery_percentage < 21
        ? patient.memory_connect.battery_percentage
        : false,
    respitePartnerData: patientProfile?.respite_partner,
    selectedTaskId: taskId,
    handleCloseDrawer,
    summaryTabIndex,
    taskTabIndex,
    openTaskOnCreate: tabIndex === summaryTabIndex || tabIndex === taskTabIndex,
    handleOpenCMActivitiesModal,
    handleCloseCMActivitiesModal,
    isCMActivitiesModalOpen,
    isLogCareCoordinationPending,
    handleLogCareCoordination,
  };
};
